import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { deleteApiKeys, recreateApiKeys } from "src/ui/containers/account/api";
import { copyToClipboard } from "src/ui/utils/clipboard";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { DangerButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useAccountApiKeys } from "../../hooks/useAccountApiKeys";

const useStyles = makeStyles()((theme: Theme) => ({
    loading: {
        display: "flex",
        width: "100%",
        height: "80vh",
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        width: "100%",
    },
    apiKeyContainer: {
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    apiKeyActions: {
        display: "flex",
        gap: theme.spacing(1),
    },
}));

function AccountApiKeyPage() {
    const [isRemovingApiKey, setIsRemovingApiKey] = React.useState(false);
    const [isRenewApiKey, setIsRenewApiKey] = React.useState(false);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const { apiKeys, setApiKeys, isLoading } = useAccountApiKeys();

    const handleApiKeysRenew = async () => {
        setIsRenewApiKey(true);

        try {
            const { model, message } = await recreateApiKeys();
            setApiKeys(model);
            showSuccessToast(message);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRenewApiKey(false);
        }
    };
    const handleApiKeysDeletion = async () => {
        setIsRemovingApiKey(true);

        try {
            const { message } = await deleteApiKeys();
            showSuccessToast(message);
            setApiKeys("");
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRemovingApiKey(false);
        }
    };

    return (
        <PageContainer fullWidth={false} isLoading={isLoading}>
            <SectionPanel title={t("pages:settingsPage.apiKey.title")}>
                <Box className={classes.apiKeyContainer}>
                    {apiKeys ? (
                        <>
                            <span>{apiKeys}</span>
                            <div className={classes.apiKeyActions}>
                                <SecondaryButton
                                    startIcon={<i className="fa-solid fa-copy" />}
                                    name="copy-api-key"
                                    onClick={() => void copyToClipboard(apiKeys)}
                                >
                                    {t("common:button.copy")}
                                </SecondaryButton>
                                <SecondaryButton
                                    startIcon={<i className="fa-solid fa-arrow-rotate-left" />}
                                    name="renew-api-key"
                                    onClick={() => handleApiKeysRenew()}
                                    loading={isRenewApiKey}
                                    disabled={isRenewApiKey || isRemovingApiKey}
                                    size="small"
                                >
                                    {t("pages:settingsPage.apiKey.renew")}
                                </SecondaryButton>
                                <DangerButton
                                    disabled={!apiKeys || isRemovingApiKey || isRenewApiKey}
                                    size="small"
                                    startIcon={<i className="fas fa-fw fa-trash-alt" />}
                                    name="delete-api-key"
                                    onClick={() => handleApiKeysDeletion()}
                                    loading={isRemovingApiKey}
                                >
                                    {t("common:button.delete")}
                                </DangerButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <span>{t("pages:settingsPage.apiKey.noKey")}</span>
                            <PrimaryOutlinedButton
                                startIcon={<i className="fa-solid fa-key" />}
                                onClick={() => handleApiKeysRenew()}
                                loading={isRenewApiKey}
                                disabled={isRenewApiKey || isRemovingApiKey}
                                size="small"
                                name="generate-api-key"
                            >
                                {t("common:button.generate")}
                            </PrimaryOutlinedButton>
                        </>
                    )}
                </Box>
            </SectionPanel>
        </PageContainer>
    );
}

export default withMetaDescription(AccountApiKeyPage, {
    title: i18next.t("pages:settingsPage.pages.apikey.metaTitle"),
    description: i18next.t("pages:settingsPage.pages.apikey.metaDescription"),
});
